import type { DownloadErrorCode } from "@volgo/core/errors";
import { ApiError } from "../api/client";

export const REMOVED_GALLERY_TEXT = "원본 사이트에서 삭제된 갤러리입니다. 검색 기본 목록에서 숨겼습니다.";

/** Every message says what went wrong and what to do next. */
const API_TEXT: Partial<Record<ApiError["code"], string>> = {
  GALLERY_REMOVED: REMOVED_GALLERY_TEXT,
  UNAVAILABLE: "인증 서비스를 확인할 수 없습니다. 잠시 후 다시 시도하세요.",
  NETWORK: "서버에 연결하지 못했습니다. 인터넷 연결을 확인하고 다시 시도하세요.",
  UNAUTHENTICATED: "로그인이 끝났습니다. 다시 로그인하세요.",
  TOKEN_EXPIRED: "로그인이 끝났습니다. 다시 로그인하세요.",
  INVALID_CREDENTIALS: "접근 키가 맞지 않습니다. 키를 다시 확인하고 입력하세요.",
  ACCOUNT_DISABLED: "정지된 계정입니다. 관리자에게 문의하세요.",
  RATE_LIMITED: "요청이 너무 잦습니다. 잠시 기다렸다가 다시 시도하세요.",
  FORBIDDEN: "이 화면을 볼 권한이 없습니다. 관리자 계정으로 로그인하세요.",
  NOT_FOUND: "찾는 것이 없습니다. 주소를 확인하세요.",
  VALIDATION_FAILED: "입력한 값을 서버가 받지 않았습니다. 값을 확인하고 다시 시도하세요.",
  POLICY_BLOCKED: "차단된 태그가 포함되어 있습니다. 이 갤러리는 받거나 읽을 수 없습니다.",
  POLICY_ACK_REQUIRED: "경고 태그가 있는 갤러리입니다. 확인한 뒤에 진행하세요.",
  POLICY_FIXED: "고정 차단 태그는 바꾸거나 지울 수 없습니다.",
  CONFLICT: "지금 상태에서는 할 수 없는 변경입니다. 화면을 새로 고쳐 상태를 확인하세요.",
  DOWNLOAD_NOT_READY: "아직 다 받지 않은 갤러리입니다. 먼저 내려받으세요.",
};

const FALLBACK = "서버에서 오류가 났습니다. 잠시 뒤에 다시 시도하세요.";

export function describeError(
  error: unknown,
  overrides: Partial<Record<ApiError["code"], string>> = {},
): string {
  if (error instanceof ApiError) return overrides[error.code] ?? API_TEXT[error.code] ?? FALLBACK;
  return FALLBACK;
}

export function isApiError(error: unknown, code: ApiError["code"]): error is ApiError {
  return error instanceof ApiError && error.code === code;
}

/** The tags a policy refusal names (`details.matchedTags`); empty when the server sent none. */
export function matchedTagsOf(error: unknown): string[] {
  const tags = error instanceof ApiError ? error.details?.matchedTags : undefined;
  return Array.isArray(tags) ? tags.filter((t): t is string => typeof t === "string") : [];
}

const DOWNLOAD_TEXT: Record<DownloadErrorCode, string> = {
  GALLERY_REMOVED: REMOVED_GALLERY_TEXT,
  POLICY_BLOCKED: "차단된 태그가 포함되어 있어 받지 않았습니다.",
  GP_INSUFFICIENT: "Archive를 받을 GP가 모자랍니다. 관리자에게 알리세요.",
  ARCHIVE_FORM_CHANGED: "E-Hentai의 Archive 화면이 바뀌어 받지 못했습니다. 관리자에게 알리세요.",
  ARCHIVE_SERVER_FAILED: "Archive 서버가 파일을 주지 않았습니다. 잠시 뒤에 다시 내려받으세요.",
  HITOMI_NOT_FOUND: "과거 Hitomi 다운로드에 갤러리가 없었습니다. 다시 내려받으면 EH/ExH에서 받습니다.",
  HITOMI_RUN_FAILED: "과거 Hitomi 다운로드가 실패했습니다. 다시 내려받으면 EH/ExH에서 받습니다.",
  UPLOAD_MISMATCH: "받은 파일을 저장하다가 문제가 생겼습니다. 다시 내려받으세요.",
  EH_BLOCKED: "E-Hentai가 요청을 막았습니다. 시간이 지난 뒤에 다시 내려받으세요.",
  STALLED: "오래 응답이 없어 중단했습니다. 다시 내려받으세요.",
  INTERNAL: "서버 오류로 받지 못했습니다. 다시 내려받으세요.",
};

export function describeDownloadFailure(code: DownloadErrorCode | null): string {
  return code ? DOWNLOAD_TEXT[code] : DOWNLOAD_TEXT.INTERNAL;
}
